import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import styled from '@emotion/styled';

// Constants
const TYPING_SPEED = 60; // ms per character (customer + assistant)
const CURSOR_HIDE_DELAY = 400;
const CYCLE_PAUSE = 800;
const FADE_ANIMATION_DURATION = 700; // ms used for exit animation timing
const RETRY_DELAY = 300;
const INITIAL_MESSAGE_DELAY = 500;

const Container = styled.div`
  position: relative;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 480px;
    padding: 16px;
  }

  @media (max-width: 480px) {
    min-height: 400px;
    padding: 12px;
    border-radius: 20px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: 
      radial-gradient(ellipse 60% 80% at 48% 52%, rgba(138, 43, 226, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 70% 60% at 52% 48%, rgba(59, 130, 246, 0.18) 0%, transparent 55%),
      radial-gradient(ellipse 50% 90% at 50% 50%, rgba(147, 51, 234, 0.15) 0%, transparent 60%),
      radial-gradient(ellipse 80% 50% at 45% 55%, rgba(99, 102, 241, 0.12) 0%, transparent 65%);
    clip-path: polygon(
      30% 20%, 70% 15%, 85% 35%, 90% 60%, 
      75% 80%, 50% 90%, 25% 85%, 10% 65%, 
      15% 40%, 30% 20%
    );
    filter: blur(60px);
    opacity: 0.4;
    z-index: 0;
    animation: pulseGlow 6s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 110%;
    height: 110%;
    background: 
      radial-gradient(ellipse 55% 75% at 52% 48%, rgba(168, 85, 247, 0.15) 0%, transparent 45%),
      radial-gradient(ellipse 65% 55% at 48% 52%, rgba(79, 70, 229, 0.12) 0%, transparent 50%);
    clip-path: polygon(
      35% 25%, 65% 20%, 80% 40%, 85% 55%, 
      70% 75%, 55% 85%, 30% 80%, 15% 60%, 
      20% 45%, 35% 25%
    );
    filter: blur(80px);
    opacity: 0.3;
    z-index: 0;
    animation: pulseGlow 8s ease-in-out infinite reverse;
  }
  
  @keyframes pulseGlow {
    0%, 100% {
      opacity: 0.3;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.45;
      transform: translate(-50%, -50%) scale(1.05);
    }
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const ChatBubbleWrapper = styled(motion.div) <{ isLocked?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 85%;
`;

const AssistantMessageWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const ChatBubble = styled(motion.div)`
  background-color: #ffffff;
  border-radius: 20px;
  border-bottom-left-radius: 4px;
  padding: 20px 20px 16px;
  max-width: 85%;
  min-height: 60px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 20px;
  justify-content: center;
  z-index: 1;
  will-change: transform;

  @media (max-width: 480px) {
    padding: 14px 16px 12px;
    min-height: 48px;
    gap: 20px;
    margin-bottom: 16px;
    border-radius: 16px;
    border-bottom-left-radius: 4px;
  }
`;

const TypingDotsContainer = styled.div<{ visible: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 24px;
  position: ${props => props.visible ? 'relative' : 'absolute'};
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const Dot = styled(motion.div)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #94a3b8;
  margin: 0 2px;
`;

const TextContainer = styled.div<{ visible: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  opacity: ${props => props.visible ? 1 : 0};
  position: ${props => props.visible ? 'relative' : 'absolute'};
`;

const Text = styled.span`
  font-size: 16px;
  color: #1f2937;
  font-weight: 500;
  line-height: 24px;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Cursor = styled(motion.div)`
  width: 2px;
  height: 20px;
  background-color: #3b82f6;
  margin-left: 2px;
`;

const AssistantAvatar = styled(motion.div)`
  width: 60px;
  height: 60px;
  min-height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0);
  background-image: url('/assets/modern-bubble.png');
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    min-height: 48px;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;


const CustomerMessagesContainer = styled(motion.div) <{ isAssistantLocked?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: ${props => props.isAssistantLocked ? '10px' : '20px'};
  width: 85%;
  max-width: 85%;
  position: relative;
`;

const CustomerMessageWrapper = styled(motion.div) <{ isEven: boolean }>`
  display: flex;
  flex-direction: ${props => props.isEven ? 'row' : 'row-reverse'};
  align-items: flex-end;
  gap: 8px;
  will-change: transform, opacity;
  position: relative;
  pointer-events: ${props => props.isEven ? 'auto' : 'auto'};
`;

const CustomerAvatar = styled(motion.div) <{ bgColor: string }>`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${props => props.bgColor};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    border-radius: 14px;
  }
`;

const CustomerAvatarText = styled.span`
  font-size: 12px;
  color: #ffffff;
  font-weight: bold;

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const CustomerBubble = styled(motion.div) <{ isEven: boolean }>`
  background-color:rgba(255, 255, 255, 0.53);
  border-radius: 20px;
  padding: 12px 16px;
  max-width: 75%;
  position: relative;
  min-height: 44px;
  display: flex;
  align-items: center;

  @media (max-width: 480px) {
    padding: 10px 12px;
    min-height: 38px;
    border-radius: 16px;
    max-width: 80%;
  }
`;

const CustomerText = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  line-height: 20px;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const CustomerCursor = styled(motion.div)`
  width: 2px;
  height: 16px;
  background-color: #374151;
  margin-left: 2px;
`;

type MessageItem = {
  id: string;
  message: { text: string; color: string; avatar: string };
  position: number;
  side: 'left' | 'right';
  isFading: boolean;
  isNew: boolean;
  isTyping: boolean;
};

export default function ChatBubbleTyping() {
  const [displayedText, setDisplayedText] = useState(''); // assistant bubble typed text
  const [showCursor, setShowCursor] = useState(false); // assistant cursor
  const [showCustomerMessages, setShowCustomerMessages] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [messageQueue, setMessageQueue] = useState<MessageItem[]>([]);
  const [customerDisplayedTexts, setCustomerDisplayedTexts] = useState<{ [key: string]: string }>({});
  const [customerCursors, setCustomerCursors] = useState<{ [key: string]: boolean }>({});
  const [assistantPositionLocked, setAssistantPositionLocked] = useState(false);
  const [showAssistant, setShowAssistant] = useState(true);

  const messageCounterRef = useRef(0);
  const currentMessageIndexRef = useRef(0);
  const isProcessingRef = useRef(false);
  const lastSideRef = useRef<'left' | 'right'>('right');
  const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]);
  const assistantWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const customerMessagesRef = useRef<HTMLDivElement>(null);

  const bubbleControls = useAnimation();
  const dot1Controls = useAnimation();
  const dot2Controls = useAnimation();
  const dot3Controls = useAnimation();
  const cursorControls = useAnimation();
  const customerMessagesControls = useAnimation();
  const assistantWrapperControls = useAnimation();

  const fullText = 'Hello, how can I help you today?'; // assistant text (restored original feel)
  const allCustomerMessages = [
    { text: "Where's my order?", color: "#ff6b6b", avatar: "JS" },
    { text: "How do I return this order?", color: "#4ecdc4", avatar: "MK" },
    { text: "Unable to access my course?", color: "#45b7d1", avatar: "AL" },
    { text: "What's my account balance?", color: "#f59e0b", avatar: "RD" },
    { text: "I need help with my subscription", color: "#8b5cf6", avatar: "SM" },
    { text: "Can I get a refund?", color: "#ec4899", avatar: "TJ" },
    { text: "How do I change my password?", color: "#10b981", avatar: "KL" },
    { text: "My payment failed, what should I do?", color: "#ef4444", avatar: "NP" },
    { text: "Where can I find my invoices?", color: "#06b6d4", avatar: "QR" },
    { text: "I forgot my login credentials", color: "#f97316", avatar: "AB" },
    { text: "How do I update my profile?", color: "#a855f7", avatar: "CD" },
    { text: "What are your business hours?", color: "#14b8a6", avatar: "EF" },
  ];

  const clearAllTimeouts = () => {
    timeoutIdsRef.current.forEach(id => clearTimeout(id));
    timeoutIdsRef.current = [];
  };

  const addTimeout = (callback: () => void, delay: number) => {
    const id = setTimeout(callback, delay);
    timeoutIdsRef.current.push(id);
    return id;
  };

  const calculateTypingDuration = (text: string) => (text.length * TYPING_SPEED) + CURSOR_HIDE_DELAY;

  const addMessageToQueue = (position: number, message: typeof allCustomerMessages[0], side: 'left' | 'right', isNew: boolean = false) => {
    const messageId = `msg-${messageCounterRef.current++}`;

    setMessageQueue(prev => [...prev, {
      id: messageId,
      message,
      position,
      side,
      isFading: false,
      isNew,
      isTyping: false
    }]);

    setCustomerDisplayedTexts(prev => ({ ...prev, [messageId]: '' }));
    setCustomerCursors(prev => ({ ...prev, [messageId]: false }));

    return messageId;
  };

  const typeMessage = (messageId: string, messageText: string, startDelay: number = 300) => {
    // customer typing logic - keep as-is
    setMessageQueue(prev => prev.map(m =>
      m.id === messageId ? { ...m, isTyping: true } : m
    ));

    addTimeout(() => {
      const typeCustomerText = (textIndex: number) => {
        if (textIndex > messageText.length) return;

        setCustomerDisplayedTexts(prev => ({
          ...prev,
          [messageId]: messageText.slice(0, textIndex)
        }));

        setCustomerCursors(prev => ({
          ...prev,
          [messageId]: textIndex < messageText.length
        }));

        if (textIndex < messageText.length) {
          addTimeout(() => typeCustomerText(textIndex + 1), TYPING_SPEED);
        } else {
          setMessageQueue(prev => prev.map(m =>
            m.id === messageId ? { ...m, isTyping: false } : m
          ));
          addTimeout(() => {
            setCustomerCursors(prev => ({ ...prev, [messageId]: false }));
          }, CURSOR_HIDE_DELAY);
        }
      };
      typeCustomerText(0);
    }, startDelay);
  };

  const handleFIFOCycle = () => {
    if (isProcessingRef.current) return;

    setMessageQueue(prev => {
      if (prev.length !== 3) return prev;

      const anyTyping = prev.some(m => m.isTyping);
      if (anyTyping) {
        addTimeout(() => handleFIFOCycle(), RETRY_DELAY);
        return prev;
      }

      const position0Message = prev.find(m => m.position === 0);
      if (!position0Message) return prev;

      isProcessingRef.current = true;

      // determine the latest message by highest `position` (robust)
      const previousMessage = prev.reduce((acc, m) => {
        return (m.position > acc.position) ? m : acc;
      }, prev[0]);

      const previousSide = previousMessage?.side ?? lastSideRef.current;

      // Alternate from the previous message's side
      const newSide: 'left' | 'right' = previousSide === 'right' ? 'left' : 'right';

      // Update ref to keep it in sync for next iteration
      lastSideRef.current = newSide;

      const nextMessage = allCustomerMessages[currentMessageIndexRef.current % allCustomerMessages.length];
      currentMessageIndexRef.current = (currentMessageIndexRef.current + 1) % allCustomerMessages.length;

      // Create new message id (consistent with addMessageToQueue logic)
      const newMessageId = `msg-${messageCounterRef.current++}`;

      // Initialize state holders for the new message BEFORE we insert it
      setCustomerDisplayedTexts(prev => ({ ...prev, [newMessageId]: '' }));
      setCustomerCursors(prev => ({ ...prev, [newMessageId]: false }));

      // Mark the oldest as fading (will animate out gently), append the new message as position 2
      const updated = prev.map(m =>
        m.id === position0Message.id ? { ...m, isFading: true } : m
      );

      const newQueue: MessageItem[] = [...updated, {
        id: newMessageId,
        message: nextMessage,
        position: 2,
        side: newSide,
        isFading: false,
        isNew: true,
        isTyping: true
      }];

      // start typing animation for the newly added message
      typeMessage(newMessageId, nextMessage.text, 100);

      // Wait for the gentle fade animation to complete before mutating the queue & positions
      addTimeout(() => {
        setMessageQueue(current => {
          // Remove faded message and shift positions
          const withoutFaded = current.filter(m => m.id !== position0Message.id);

          const shifted = withoutFaded.map(m => {
            if (m.id === newMessageId) {
              return { ...m, isNew: false };
            }
            return {
              ...m,
              position: m.position === 1 ? 0 : m.position === 2 ? 1 : m.position,
              isNew: false
            };
          });

          return shifted;
        });

        // Clean up displayed text & cursor state for removed message
        setCustomerDisplayedTexts(prev => {
          const updated = { ...prev };
          delete updated[position0Message.id];
          return updated;
        });
        setCustomerCursors(prev => {
          const updated = { ...prev };
          delete updated[position0Message.id];
          return updated;
        });

        const typingDuration = calculateTypingDuration(nextMessage.text);
        const totalWaitTime = typingDuration + CYCLE_PAUSE;

        addTimeout(() => {
          setMessageQueue(current => {
            const stillTyping = current.some(m => m.isTyping);

            if (!stillTyping) {
              isProcessingRef.current = false;
              handleFIFOCycle();
            } else {
              addTimeout(() => {
                isProcessingRef.current = false;
                handleFIFOCycle();
              }, 500);
            }
            return current;
          });
        }, totalWaitTime);
      }, FADE_ANIMATION_DURATION);

      return newQueue;
    });
  };

  const lockAssistantPosition = () => {
    if (assistantWrapperRef.current && containerRef.current && customerMessagesRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;

      setAssistantPositionLocked(true);

      // Animate assistant bubble fading out completely
      assistantWrapperControls.start({
        opacity: 0,
        transition: {
          duration: 0.5,
          ease: 'easeOut',
          onComplete: () => {
            setShowAssistant(false);
          }
        }
      });

      // Calculate move-up to center customer messages vertically
      const customerRect = customerMessagesRef.current.getBoundingClientRect();
      const customerTop = customerRect.top - containerRect.top;
      const customerHeight = customerRect.height;
      // Center the customer messages: move them up so their center aligns with container center
      const targetCenter = containerHeight / 2;
      const currentCenter = customerTop + customerHeight / 2;
      const moveUpAmount = currentCenter - targetCenter;

      // Animate customer messages moving up to center
      customerMessagesControls.start({
        y: -moveUpAmount,
        transition: {
          duration: 0.5,
          ease: 'easeOut'
        }
      });
    }
  };

  const initializeCustomerMessages = useCallback(() => {
    const initialMessages = allCustomerMessages.slice(0, 3);

    lastSideRef.current = 'right';
    const msg1Id = addMessageToQueue(0, initialMessages[0], 'right');
    typeMessage(msg1Id, initialMessages[0].text, 300);

    const firstTypingDuration = calculateTypingDuration(initialMessages[0].text);
    addTimeout(() => {
      lastSideRef.current = 'left';
      const msg2Id = addMessageToQueue(1, initialMessages[1], 'left');
      typeMessage(msg2Id, initialMessages[1].text, 300);

      const secondTypingDuration = calculateTypingDuration(initialMessages[1].text);
      addTimeout(() => {
        lastSideRef.current = 'right';
        const msg3Id = addMessageToQueue(2, initialMessages[2], 'right');
        typeMessage(msg3Id, initialMessages[2].text, 300);

        const thirdTypingDuration = calculateTypingDuration(initialMessages[2].text);
        // Wait for typing to complete: startDelay (300ms) + typing duration + cursor hide delay + initial delay
        const totalTypingTime = 300 + thirdTypingDuration + INITIAL_MESSAGE_DELAY;
        addTimeout(() => {
          // Check if typing is still in progress before locking
          const checkAndLock = () => {
            setMessageQueue(current => {
              const thirdMessage = current.find(m => m.position === 2);
              const isStillTyping = thirdMessage?.isTyping || false;

              if (isStillTyping) {
                // If still typing, check again after a short delay
                addTimeout(checkAndLock, 100);
                return current;
              } else {
                // All typing complete, now lock position
                lockAssistantPosition();
                currentMessageIndexRef.current = 3;
                isProcessingRef.current = false;
                handleFIFOCycle();
                return current;
              }
            });
          };
          checkAndLock();
        }, totalTypingTime);
      }, secondTypingDuration + INITIAL_MESSAGE_DELAY);
    }, firstTypingDuration + INITIAL_MESSAGE_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Restore the original assistant typing animation sequence exactly:
    const sequence = async () => {
      // assistant bubble pop-in
      await bubbleControls.start({
        scale: 1,
        transition: { delay: 0.3, duration: 0.5, type: 'spring', stiffness: 300, damping: 20 }
      });

      // typing dots animation (keeps original feel)
      const dotAnimation = {
        opacity: [0.3, 1, 0.3],
        transition: {
          duration: 0.4,
          repeat: 3,
          ease: 'easeInOut'
        }
      };

      dot1Controls.start(dotAnimation);
      dot2Controls.start({ ...dotAnimation, transition: { ...dotAnimation.transition, delay: 0.15 } });
      dot3Controls.start({ ...dotAnimation, transition: { ...dotAnimation.transition, delay: 0.3 } });

      // hide dots and show assistant typing text with blinking cursor (original flow)
      addTimeout(() => {
        dot1Controls.start({ opacity: 0, transition: { duration: 0.2 } });
        dot2Controls.start({ opacity: 0, transition: { duration: 0.2 } });
        dot3Controls.start({ opacity: 0, transition: { duration: 0.2 } });

        addTimeout(() => {
          setShowCursor(true);
          cursorControls.start({
            opacity: [1, 0],
            transition: {
              duration: 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          });

          // type assistant text char-by-char (restored original behavior)
          let currentIndex = 0;
          const typeText = () => {
            if (currentIndex <= fullText.length) {
              setDisplayedText(fullText.slice(0, currentIndex));
              currentIndex++;
              addTimeout(typeText, TYPING_SPEED);
            } else {
              addTimeout(() => {
                setShowCursor(false);
                cursorControls.stop();

                // after typing assistant text, show customer messages just like before
                addTimeout(() => {
                  setShowCustomerMessages(true);
                  customerMessagesControls.start({
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.4 }
                  });

                  initializeCustomerMessages();
                }, 800);
              }, 600);
            }
          };
          typeText();
        }, 200);
      }, 1500);
    };

    sequence();

    return () => {
      clearAllTimeouts();
    };
  }, [bubbleControls, cursorControls, customerMessagesControls, dot1Controls, dot2Controls, dot3Controls, initializeCustomerMessages]);

  return (
    <Container ref={containerRef}>
      {showAssistant && (
        <ChatBubbleWrapper
          ref={(el: HTMLDivElement | null) => {
            if (el) assistantWrapperRef.current = el;
          }}
          isLocked={assistantPositionLocked}
          animate={assistantWrapperControls}
          initial={{ y: 0, opacity: 1 }}
        >
          <AssistantMessageWrapper
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <AssistantAvatar
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1, type: 'spring', stiffness: 200 }}
            >
              {logoError ? (
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '12px' }}>AI</span>
              ) : (
                <AvatarImage
                  src="/assets/modern-bubble.png"
                  alt="Assistant"
                  onError={() => setLogoError(true)}
                />
              )}
            </AssistantAvatar>

            <ChatBubble
              initial={{ scale: 0 }}
              animate={bubbleControls}
            >

              {/* typing dots shown while assistant hasn't started typing text */}
              <TypingDotsContainer visible={!displayedText}>
                <Dot animate={dot1Controls} initial={{ opacity: 0 }} />
                <Dot animate={dot2Controls} initial={{ opacity: 0 }} />
                <Dot animate={dot3Controls} initial={{ opacity: 0 }} />
              </TypingDotsContainer>

              {/* restored original assistant typing text + blinking cursor */}
              <TextContainer visible={!!displayedText}>
                <Text>{displayedText}</Text>
                {showCursor && (
                  <Cursor
                    animate={cursorControls}
                    initial={{ opacity: 0 }}
                  />
                )}
              </TextContainer>
            </ChatBubble>
          </AssistantMessageWrapper>
        </ChatBubbleWrapper>
      )}

      {showCustomerMessages && (
        <CustomerMessagesContainer
          ref={customerMessagesRef}
          isAssistantLocked={assistantPositionLocked}
          initial={{ opacity: 0, y: 0 }}
          animate={customerMessagesControls}
        >


          <AnimatePresence mode="sync" initial={false}>
            {messageQueue
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((queueItem) => {
                const { id, message, side, isFading, isNew } = queueItem;
                const isEven = side === 'right';
                const displayedText = customerDisplayedTexts[id] || '';
                const showCursor = customerCursors[id] || false;

                return (
                  <CustomerMessageWrapper
                    key={id}
                    layout // enable layout animations so reflows are smoothly animated
                    isEven={isEven}
                    initial={isNew
                      ? { opacity: 0, y: 8, scale: 0.985 }
                      : { opacity: 0, y: 4, scale: 0.995 }
                    }
                    animate={isFading
                      ? { opacity: 0, y: -12, scale: 0.98, pointerEvents: 'none' }
                      : { opacity: 1, y: 0, scale: 1, pointerEvents: 'auto' }
                    }
                    exit={{ opacity: 0, y: -12, scale: 0.98 }}
                    transition={
                      isFading
                        ? {
                          opacity: { duration: FADE_ANIMATION_DURATION / 1000, ease: 'easeOut' },
                          y: { type: 'tween', duration: FADE_ANIMATION_DURATION / 1000, ease: 'easeOut' },
                          scale: { duration: FADE_ANIMATION_DURATION / 1000, ease: 'easeOut' },
                          layout: { type: 'spring', stiffness: 220, damping: 28 }
                        }
                        : {
                          opacity: { duration: 0.28 },
                          y: { type: 'spring', stiffness: 300, damping: 28, duration: 0.5 },
                          scale: { duration: 0.28 },
                          layout: { type: 'spring', stiffness: 300, damping: 28 }
                        }
                    }
                  >
                    <CustomerAvatar
                      bgColor={message.color}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 25
                      }}
                    >
                      <CustomerAvatarText>{message.avatar}</CustomerAvatarText>
                    </CustomerAvatar>

                    <CustomerBubble
                      isEven={isEven}
                      initial={{ scale: 0.98, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        duration: 0.2,
                        ease: 'easeOut'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <CustomerText>{displayedText}</CustomerText>
                        {showCursor && (
                          <CustomerCursor
                            animate={{ opacity: [1, 0] }}
                            transition={{
                              duration: 0.5,
                              repeat: Infinity,
                              ease: 'easeInOut'
                            }}
                          />
                        )}
                      </div>
                    </CustomerBubble>
                  </CustomerMessageWrapper>
                );
              })}
          </AnimatePresence>
        </CustomerMessagesContainer>
      )}
    </Container>
  );
}
